import axios, { AxiosRequestConfig } from 'axios';

import {
  DatabaseContext,
  ImportMapping,
  ImportMappings,
  LastUserEntryDetails,
  PersonalConfiguration,
  PrimaryKey,
  ProjectMapping,
  ProjectsMappings,
} from '../types';
import { chunkArray } from '../utils/utils';

export interface JSONResponse {
  data?: unknown;
}

export interface GlobalConfiguration {
  billable: boolean;
  applyJIRAIssueLabels: boolean;
  mapProjectsForAll: boolean;
  addTagSameAsEpicName: boolean;
  addTagSameAsIssueType: boolean;
  roundTime: boolean;
  jiraTimeZone: string;
}

interface GlobalConfigurationResponse {
  data: GlobalConfiguration | undefined;
}

export async function authenticatedApiRequest<T>(
  url: string,
  method: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH',
  requestData?: unknown
) {
  try {
    const token = await AP.context.getToken();
    const headers = {
      Authorization: `JWT ${token}`,
      contentType: 'application/json',
    };

    const config: AxiosRequestConfig = {
      method: method.toUpperCase(),
      url,
      headers,
      data: requestData ?? undefined,
    };

    const { data }: JSONResponse = await axios(config);
    return data as T;
  } catch (error) {
    console.error('Error making API request:', error);
    throw error;
  }
}

export async function getGlobalConfiguration() {
  const data = await authenticatedApiRequest<GlobalConfigurationResponse>(
    '/api/globalconfiguration',
    'GET'
  );
  return data.data;
}

export async function setGlobalConfiguration(
  globalConfig: GlobalConfiguration
) {
  const data = await authenticatedApiRequest<GlobalConfigurationResponse>(
    '/api/globalconfiguration',
    'POST',
    { globalConfig }
  );
  return data.data;
}

export const updatePersonalConfiguration = async (
  config: Partial<
    Record<
      keyof PersonalConfiguration,
      PersonalConfiguration[keyof PersonalConfiguration] | null
    >
  >
) => {
  return authenticatedApiRequest('/api/personal-config', 'PATCH', { config });
};

export const updateSyncConfiguration = async ({
  syncByCron,
}: Partial<PersonalConfiguration>) => {
  return authenticatedApiRequest('/api/sync-by-cron', 'PATCH', { syncByCron });
};

export const getSyncByCronConfiguration = async () => {
  return authenticatedApiRequest<{ syncByCron: boolean }>(
    '/api/sync-by-cron',
    'GET'
  );
};

type PersonalConfigurationApiResponse = PersonalConfiguration &
  PrimaryKey & { userAccountId: string };

export const getPersonalConfiguration = async () => {
  return authenticatedApiRequest<PersonalConfigurationApiResponse>(
    '/api/personal-config',
    'GET'
  );
};

export const getProjectMapping = async (
  isGlobal: boolean = false,
  jiraProjectKey: string
) => {
  const path = `/api/${
    isGlobal ? 'global' : 'personal'
  }-projects-mappings/${jiraProjectKey}`;
  return authenticatedApiRequest<ProjectMapping>(path, 'GET');
};

export const getProjectsMappings = async (isGlobal: boolean = false) => {
  const path = `/api/${isGlobal ? 'global' : 'personal'}-projects-mappings`;
  return authenticatedApiRequest<ProjectsMappings>(path, 'GET');
};

export const updateProjectsMappings = async (
  mappings: ProjectMapping,
  isGlobal: boolean = false
) => {
  const path = `/api/${isGlobal ? 'global' : 'personal'}-projects-mappings/${
    mappings.jiraProjectKey
  }`;
  return authenticatedApiRequest(path, 'PUT', mappings);
};

export const deleteProjectsMappings = async (
  jiraProjectKey: string,
  isGlobal: boolean = false
) => {
  const path = `/api/${
    isGlobal ? 'global' : 'personal'
  }-projects-mappings/${jiraProjectKey}`;
  return authenticatedApiRequest(path, 'DELETE');
};

export const deleteAllProjectsMappings = async (isGlobal: boolean = false) => {
  const path = `/api/${isGlobal ? 'global' : 'personal'}-projects-mappings`;
  return authenticatedApiRequest(path, 'DELETE');
};

export const getImportMappings = async (timeEntriesIds: string[]) => {
  const chunks = chunkArray(timeEntriesIds, 500);
  let result = {};
  for (const chunk of chunks) {
    const mappings = await authenticatedApiRequest<ImportMappings>(
      '/api/import-mapping',
      'POST',
      { timeEntriesIds: chunk }
    );
    result = {
      ...result,
      ...mappings,
    };
  }
  return result;
};

export const getDatabaseContext = async (): Promise<
  Omit<DatabaseContext, 'importMappings'>
> => {
  const promises: [
    Promise<GlobalConfiguration | undefined>,
    Promise<PersonalConfiguration>,
    Promise<{ syncByCron: boolean }>
  ] = [
    getGlobalConfiguration(),
    getPersonalConfiguration(),
    getSyncByCronConfiguration(),
  ];
  const [globalConfiguration, personalConfiguration, syncConfig] =
    await Promise.all(promises);
  return {
    globalConfiguration,
    personalConfiguration: { ...personalConfiguration, ...syncConfig },
  };
};

export const putImportMappings = async (mappings: ImportMapping[]) =>
  authenticatedApiRequest('/api/import-mapping', 'PUT', { mappings });

export const deleteImportMapping = async (togglTimeEntryId: number) =>
  authenticatedApiRequest('/api/import-mapping', 'DELETE', {
    togglTimeEntryId,
  });

export async function getUsersDetails(ExclusiveStartKey: string | null = '') {
  const url = ExclusiveStartKey
    ? `/api/users?startKey=${ExclusiveStartKey}`
    : 'api/users';
  const data = await authenticatedApiRequest<{
    users: LastUserEntryDetails[];
    lastEvaluatedKey: string;
  }>(url, 'GET');
  return data;
}
